import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../../context/authContext";
import styles from "./AddEditTodoDialog.module.scss";
import _ from "lodash";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import gql from "graphql-tag";
import useForm from "../../hooks/useForm";
import { useDepartmentFilter } from "../../context/departmentFilterContext";
import { isAuthed } from "../../utils/authorization";
import * as Yup from "yup";
import { useFormik, FormikProvider } from "formik";
import { getPlansByReferenceModel, isValidDateInteger, recursiveMergeCustomizer } from "../../utils/misc";
import { TODO_FIELDS } from "../../utils/fragments";
import { Link } from "react-router-dom";

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Tooltip,
  useMediaQuery,
  Typography,
  useTheme,
} from "@material-ui/core";
import { Autocomplete, ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import Icon from "@mdi/react";
import {
  mdiLinkVariantOff,
  mdiRhombus,
  mdiRhombusSplit,
  mdiPoll,
  mdiBullseyeArrow,
  mdiAlertDecagram,
  mdiCheckboxMarked,
  mdiChartTimelineVariant,
} from "@mdi/js";

import SelectUsers from "../SelectUsers/SelectUsers";
import SelectDepartment from "../SelectDepartment/SelectDepartment";
import Loading from "../Loading/Loading";
import PlanPill from "../PlanPill/PlanPill";
import NewSelectDepartment from "../SelectDepartment/NewSelectDepartment";
import NewSelectUsers from "../SelectUsers/NewSelectUsers";
import ReferenceSelector from "../ReferenceSelector/ReferenceSelector";

const PRIORITY = ["high", "medium", "low"];

const AddEditTodoDialog = ({ dialog, setDialog, requestFetch, fetch, params, org, snack }) => {
  const fs = useMediaQuery("(max-width: 600px)");
  const theme = useTheme();

  const fiscalYear = org.fiscalYear;

  const { auth } = useAuth();
  const isAdmin = isAuthed(auth, "department facilitator");

  const { departmentFilter } = useDepartmentFilter();
  const deptFilterSpid = _.get(departmentFilter, "sharedPlanId");

  const todoDialog = _.get(dialog, "addTodoDialog", {});
  const { open, todo, planId, category: ctgry, referenceId, referenceModel, value, user } = todoDialog;

  const isCreate = _.isNil(todo);

  const category = _.get(todo, "category", ctgry);
  const isIssue = category === "issue";

  const [close, setClose] = useState(true);
  const [triggerRefetch, setTriggerRefetch] = useState(false);

  const [createTodo, { loading }] = useMutation(CREATE_TODO);
  const [updateTodo] = useMutation(UPDATE_TODO);

  const {
    data: oneYearCorpPlanData,
    refetch: oneYearCorpPlanDataRefetch,
    loading: oneYearCorpPlanDataLoading,
  } = useQuery(GET_ONE_YEAR_CORP_PLAN, {
    variables: {
      id: planId || null,
    },
  });

  const {
    data: userPlanData,
    refetch: userPlanDataRefetch,
    loading: userPlanDataLoading,
  } = useQuery(GET_USERS_PLANS, {
    variables: { organization: params.org, corpPlan: planId || null },
  });

  const handleClose = () => {
    setDialog((prev) => ({ ...prev, addTodoDialog: { ...prev.addTodoDialog, open: false } }));
  };

  // FORMIK START
  const getSchema = (isIssue) => {
    return Yup.object().shape({
      value: Yup.string().required(`${isIssue ? "Issue" : "To do"} details are required`),
      description: Yup.string().nullable(),
      referenceId: Yup.string().nullable(),
      referenceModel: Yup.string().nullable(),
      priority: Yup.string().required("Priority is required"), // should be fine without because init value is set to "medium"
      user: Yup.string().required("User is required"),
      plan: Yup.string().nullable(),
      done: Yup.boolean(),
    });
  };

  const getInitialValues = ({ todo, value, user, referenceId, referenceModel }) => {
    let initVals = {
      value: value || "",
      description: "",
      referenceId: referenceId || null,
      referenceModel: referenceModel || null,
      priority: "medium",
      user: user || "",
      plan: null,
      done: false,
    };

    if (!_.isNil(todo)) {
      initVals = _.mergeWith({}, initVals, todo, (objVal, srcVal) => recursiveMergeCustomizer(srcVal));
    }

    return initVals;
  };

  const formik = useFormik({
    initialValues: getInitialValues({ todo, value, user, referenceId, referenceModel }),
    validationSchema: getSchema(isIssue),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const { value, description, user, referenceId, referenceModel, priority, plan, done } = values;

      try {
        let res;
        if (isCreate) {
          res = await createTodo({
            variables: {
              organization: params.org,
              category,
              value,
              description,
              priority,
              plan,
              user: isAdmin ? user : auth.id,
              author: auth.id,
              referenceId: referenceModel ? referenceId : null,
              referenceModel,
            },
          });
        } else {
          res = await updateTodo({
            variables: {
              id: todo.id,
              value,
              description,
              priority,
              plan,
              user,
              done,
              referenceId,
              referenceModel,
            },
          });
        }

        if (res) {
          if (referenceId && isIssue) {
            snack(
              <>
                Issue Created! Click{" "}
                <Link style={{ color: "white", textDecoration: "none" }} to="issues">
                  <strong>here</strong>
                </Link>{" "}
                to view.
              </>
            );
          } else {
            snack(`${isCreate ? "Created" : "Updated"} "${value}" ${category}`);
          }

          if (close) {
            handleClose();
          } else {
            resetForm();
          }

          requestFetch();
        }
        setSubmitting(false);
      } catch (err) {
        console.error(err);
        snack(`Failed to ${isCreate ? "create" : "update"} ${category}`, "error");
      }
    },
    enableReinitialize: true,
  });

  const {
    values,
    errors,
    touched,
    handleSubmit: handleSubmitFormik,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    setValues,
    setFieldTouched,
    resetForm,
  } = formik;

  const isTodoReference = ["Todo", "Issue"].includes(values.referenceModel);

  const handleSubmit = (close) => {
    setClose(close);
    handleSubmitFormik();
  };

  const handleChangeReferenceId = (item) => {
    // change tied in reference id
    let newValues = _.cloneDeep(values);

    newValues.referenceId = _.get(item, "id", null);

    // also change department based on tied in selected
    const plans = getPlansByReferenceModel(values.referenceModel, item);
    const firstPlan = _.first(plans);

    if (!_.isNil(firstPlan) && _.isNil(values.plan)) {
      const sharedPlanId = _.get(firstPlan, "sharedPlanId");
      const targetPlan = _.find(_.get(userPlanData, "plans", []), (plan) => plan.sharedPlanId === sharedPlanId);

      if (targetPlan) {
        newValues.plan = _.get(targetPlan, "id", null);
      }
    }

    setValues(newValues);
  };

  const isCurrentYear = useMemo(() => {
    if (!oneYearCorpPlanData) return false;

    const oneYearCorpPlanYear = _.get(oneYearCorpPlanData, "plan.year");
    return isValidDateInteger(oneYearCorpPlanYear) && isValidDateInteger(fiscalYear) && oneYearCorpPlanYear === fiscalYear;
  }, [oneYearCorpPlanData, fiscalYear]);

  useEffect(() => {
    if (open) {
      setTriggerRefetch((prev) => !prev);
    } else {
      resetForm();
    }
  }, [fetch, open, resetForm]);

  useEffect(() => {
    if (open && isCreate && userPlanData && deptFilterSpid) {
      const plans = _.get(userPlanData, "plans", []);

      const planWithSameSpid = _.find(plans, (plan) => plan.sharedPlanId === deptFilterSpid);
      setFieldValue("plan", _.get(planWithSameSpid, "id", null));
    }
  }, [open, isCreate, userPlanData, deptFilterSpid, setFieldValue]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      fullWidth
      fullScreen={fs}
    >
      <DialogTitle>
        <div className={styles.title}>
          {isCreate ? "Create" : "Edit"} {isIssue ? "Issue" : "To Do"}
          <div>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <FormikProvider value={formik}>
          <TextField
            autoFocus
            multiline
            fullWidth
            label={`${isIssue ? "Issue" : "To Do"} Details`}
            variant="outlined"
            margin="normal"
            {...getFieldProps("value")}
            error={Boolean(touched.value && errors.value)}
            helperText={touched.value && errors.value}
          />
          <TextField
            label={`${isIssue ? "Issue" : "To Do"} Description`}
            fullWidth
            multiline
            {...getFieldProps("description")}
            variant="outlined"
            margin="normal"
          />

          {/* <TextField
          select
          fullWidth
          label="Priority"
          variant="outlined"
          margin="normal"
          {...getFieldProps("priority")}
          error={Boolean(touched.priority && errors.priority)}
          helperText={touched.priority && errors.priority}
        >
          {PRIORITY.map((value) => {
            return (
              <MenuItem key={value} value={value}>
                {_.startCase(value)}
              </MenuItem>
            );
          })}
        </TextField> */}
          <div style={{ marginTop: theme.spacing(1), textAlign: "center" }}>
            <ToggleButtonGroup
              exclusive
              value={values.priority}
              onChange={(e, value) => {
                setFieldTouched("priority");
                if (value !== null) {
                  setFieldValue("priority", value);
                }
              }}
              style={{
                flexWrap: "wrap",
                gap: theme.spacing(1),
                justifyContent: "center",
              }}
            >
              {PRIORITY.map((priority) => {
                return (
                  <ToggleButton key={priority} value={priority} style={{ border: `1px solid ${theme.palette.grey["A100"]}` }}>
                    {_.startCase(priority)}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
            {Boolean(touched.priority && errors.priority) && (
              <Typography
                variant="caption"
                style={{
                  display: "block",
                  color: theme.palette.error.main,
                  textAlign: "center",
                }}
              >
                {errors.priority}
              </Typography>
            )}
          </div>

          {userPlanData && (
            <>
              <NewSelectDepartment
                showAll
                buttonGroup
                name="plan"
                plans={_.get(userPlanData, "plans")}
                formik={formik}
                disabled={!_.isNil(deptFilterSpid)}
              />
              <NewSelectUsers
                buttonGroup
                name="user"
                users={_.get(userPlanData, "users")}
                handleResetField={() => setFieldValue("user", "")}
                helperText={`Who is accountable for this  ${isIssue ? "issue" : "todo"}?`}
                plan={values.plan}
                allPlans={_.get(userPlanData, "plans")}
                formik={formik}
                debug="todos"
              />
            </>
          )}
          <ReferenceSelector
            formik={formik}
            refTypeName="referenceModel"
            refIdName="referenceId"
            triggerRefetch={triggerRefetch}
            orgId={params.org}
            currCorpPlanId={planId}
            deptFilterSpid={deptFilterSpid}
            disabled={!isCurrentYear}
            handleChangeRefId={(e, newValue) => handleChangeReferenceId(newValue)}
          />
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        {isCreate ? (
          <Button
            onClick={() => handleSubmit(false)}
            color="primary"
            variant="outlined"
            disabled={loading || isSubmitting}
            className={styles.button}
          >
            {loading ? <Loading size={24} color="#fff" /> : "Create & Add Another"}
          </Button>
        ) : (
          <Button
            onClick={() => {
              setFieldValue("done", !values.done);
              setTimeout(() => {
                handleSubmit(true);
              }, 0);
            }}
            color="primary"
            style={{ marginRight: "auto" }}
          >
            {loading ? <Loading size={24} color="primary" /> : `Mark as ${values.done ? "Incomplete" : "Complete"}`}
          </Button>
        )}
        <Button
          onClick={() => handleSubmit(true)}
          color="primary"
          variant="contained"
          disabled={loading || isSubmitting}
          className={styles.button}
        >
          {loading ? <Loading size={24} color="#fff" /> : isCreate ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditTodoDialog;

const GET_ONE_YEAR_CORP_PLAN = gql`
  query ($id: ID) {
    plan(id: $id) {
      year
    }
  }
`;

const GET_USERS_PLANS = gql`
  query AddTodoDialog_GetUsersPlans($organization: ID!, $corpPlan: ID) {
    users(organization: $organization) {
      name {
        first
        last
      }
      profilePicture
      id
      plan {
        id
        departmentName
        sharedPlanId
      }
    }
    plans(organization: $organization, category: "1 year", oneYearCorpPlan: $corpPlan) {
      id
      departmentName
      sharedPlanId
      year
    }
  }
`;

const CREATE_TODO = gql`
  mutation AddTodoDialog_CreateTodo(
    $organization: ID!
    $value: String!
    $description: String
    $category: String
    $user: ID!
    $author: ID!
    $referenceId: ID
    $referenceModel: String
    $priority: String
    $plan: ID
  ) {
    createTodo(
      organization: $organization
      value: $value
      description: $description
      category: $category
      user: $user
      author: $author
      referenceId: $referenceId
      referenceModel: $referenceModel
      priority: $priority
      plan: $plan
    )
  }
`;

// const GET_REFERENCED_TODO_CREATE = gql`
//   query AddTodoDialog_GetRefTodoCreate($id: ID) {
//     todo(id: $id) {
//       id: _id
//       value
//       plan {
//         id
//         shortName
//         color
//         departmentName
//         sharedPlanId
//       }
//     }
//   }
// `;

const UPDATE_TODO = gql`
  ${TODO_FIELDS}
  mutation TodosEditDialog_UpdateTodo(
    $id: ID!
    $value: String
    $description: String
    $done: Boolean
    $user: ID!
    $referenceId: ID
    $referenceModel: String
    $priority: String
    $plan: ID
  ) {
    updateTodo(
      id: $id
      value: $value
      description: $description
      done: $done
      user: $user
      referenceId: $referenceId
      referenceModel: $referenceModel
      priority: $priority
      plan: $plan
    ) {
      todo {
        ...TodoFields
      }
      successCriterias {
        id
        todos {
          id: _id
        }
      }
      rocks {
        id
        todos {
          id: _id
        }
      }
      metrics {
        id
        todos {
          id: _id
        }
      }
      objectives {
        id
        todos {
          id: _id
        }
      }
    }
  }
`;
